import React, {useMemo, useState} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {GET_SUPPLIER_LIST} from "../../../utils/graphql";
import {apollo} from "../../../utils/apollo";
import SortIcon from "../../../components/SortIcon";
import PaginationFull from "../../../components/PaginationFull";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import {UsersIcon, ChartBarIcon, PlusIcon} from '@heroicons/react/24/outline';
import Button from "../../../components/Button";
import useAuthStore from "../../../store/auth";
import {Navigate} from "react-router-dom";
import Loading from "../../../components/Loading";
import {cn} from "../../../utils/style";

function SubVendors() {
    const {t} = useTranslation();
    const {merchant, roles, permissions} = useAuthStore();

    const [sort, setSort] = useState({field: 'id', isAscending: false});
    const [page, setPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const tableHeader = [
        {column: 'supplier_uuid', title: t('sub_vendors.table.supplierUuid'), sortable: false},
        {column: 'supplier_name', title: t('sub_vendors.table.supplierName'), sortable: false},
        {column: 'business_name', title: t('sub_vendors.table.businessName'), sortable: false},
        {column: 'email', title: t('sub_vendors.table.email'), sortable: false},
        {column: 'phone', title: t('sub_vendors.table.phone'), sortable: false},
        {column: 'active', title: t('sub_vendors.table.active'), sortable: false},
        {column: 'display_supplier_details', title: t('sub_vendors.table.displaySupplierDetails'), sortable: false},
        {column: 'actions', title: '', sortable: false},
    ]

    const fetchSupplierList = useQuery(
        ['suppliers_list', {page, sort}],
        async () => {
            const {data} = await apollo.query({
                query: GET_SUPPLIER_LIST,
                variables: {
                    sortBy: sort.field?.toUpperCase(),
                    sortDirection: sort.isAscending ? "ASC" : "DESC",
                    page,
                    first: 15,
                    filter: {}
                },
            });
            return data?.getSupplierList;
        },
        {keepPreviousData: false}
    );
    const records = useMemo(() => fetchSupplierList?.isSuccess ? fetchSupplierList?.data : null, [fetchSupplierList]);

    const hasAccess = roles?.includes("merchant") || permissions?.includes("view_suppliers");
    if (!hasAccess || merchant?.allowSubVendors === false) {
        return <Navigate to={'/dashboard'} replace/>
    }

    const handleChangeSort = (field) => {
        if (sort.field === field) {
            setSort(prevState => ({...prevState, isAscending: !prevState.isAscending}));
        } else {
            setSort({field: field, isAscending: true});
        }
    };

    return (
        <DashboardLayout>
            <div>
                <div className="mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-white shadow-sm border border-gray-100 text-[#0b6a8c]">
                            <UsersIcon className="h-7 w-7"/>
                        </div>
                        <div>
                            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('sub_vendors.page_title')}</h2>
                            <p className="text-gray-500 font-medium">{t('sub_vendors.page_description')}</p>
                        </div>
                    </div>
                    <ButtonInternalLink
                        href="/merchant/sub-vendors/create"
                        className="rounded-2xl px-6 py-3 shadow-lg shadow-blue-600/10 flex items-center gap-2"
                    >
                        <PlusIcon className="h-5 w-5" />
                        {t('sub_vendors.create_sub_vendor')}
                    </ButtonInternalLink>
                </div>

                {fetchSupplierList.isLoading && (
                    <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center">
                        <Loading message={t('sub_vendors.loading_title')} fullHeight={false} />
                    </div>
                )}

                {(fetchSupplierList.isSuccess && (!records || !records?.data?.length)) && (
                    <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center">
                        <div className="mx-auto h-20 w-20 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100 shadow-inner text-gray-400">
                            <UsersIcon className="h-10 w-10" />
                        </div>
                        <h3 className="mt-8 text-xl font-bold text-gray-900">{t('sub_vendors.no_data_title')}</h3>
                        <p className="mt-2 text-gray-500 font-medium">{t('sub_vendors.no_data_description')}</p>
                        <div className="mt-8">
                            <Button
                                onClick={() => fetchSupplierList.refetch()}
                                className="rounded-2xl px-8"
                            >
                                {t('sub_vendors.refresh_data')}
                            </Button>
                        </div>
                    </div>
                )}

                {(fetchSupplierList.isSuccess && records && records?.data?.length > 0) && (
                    <div className="bg-white/40 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-8 py-5 border-b border-gray-100/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                                        <UsersIcon className="h-5 w-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900">{t('sub_vendors.sub_vendors_list')}</h3>
                                </div>
                                <span className="text-sm font-bold text-[#0b6a8c] bg-cyan-50 px-4 py-1.5 rounded-full">
                                    {records?.paginatorInfo?.total || records?.data?.length || 0} {t('sub_vendors.records')}
                                </span>
                            </div>
                        </div>
                        <div className="overflow-x-auto p-4">
                            <table className="min-w-full border-separate border-spacing-y-3">
                                <thead>
                                <tr>
                                    {tableHeader?.map(item => (
                                        <th
                                            key={item.column}
                                            scope="col"
                                            className="px-6 py-2 ltr:text-left rtl:text-right text-xs font-bold text-gray-400 uppercase tracking-widest"
                                        >
                                            {!item.hidden && (
                                                <>
                                                    {item.sortable ? (
                                                        <button
                                                            onClick={() => handleChangeSort(item.column)}
                                                            type="button"
                                                            className="group inline-flex items-center hover:text-[#0b6a8c] transition-colors"
                                                        >
                                                            {item.title}
                                                            <SortIcon sort={sort} field={item.column}/>
                                                        </button>
                                                    ) : (
                                                        <span>{item.title}</span>
                                                    )}
                                                </>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {records?.data?.map((record) => (
                                    <tr key={record?.supplierUuid} className="group transition-all duration-300">
                                        <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white rounded-l-3xl transition-all border-y border-l border-gray-50 group-hover:border-cyan-50">
                                            <div className="text-sm font-mono font-bold text-gray-900">{record?.supplierUuid}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                            <div className="text-sm font-bold text-gray-900">{record?.supplierName}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                            <div className="text-sm font-medium text-gray-600">{record?.businessName}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                            <div className="text-sm text-gray-500 font-medium">{record?.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                            <div className="text-sm text-gray-500 font-medium">{record?.phone}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                            {record?.active ? (
                                                <div className="px-3 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 inline-block uppercase tracking-wider">
                                                    {t('sub_vendors.active')}
                                                </div>
                                            ) : (
                                                <div className="px-3 py-1 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-100 inline-block uppercase tracking-wider">
                                                    {t('sub_vendors.inactive')}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
                                            {record?.displaySupplierDetails ? (
                                                <div className="px-3 py-1 rounded-full text-[10px] font-bold bg-cyan-50 text-[#0b6a8c] border border-cyan-100 inline-block uppercase tracking-wider">
                                                    {t('refund.approve')}
                                                </div>
                                            ) : (
                                                <div className="px-3 py-1 rounded-full text-[10px] font-bold bg-gray-50 text-gray-400 border border-gray-100 inline-block uppercase tracking-wider">
                                                    {t('refund.reject')}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap bg-white/80 group-hover:bg-white rounded-r-3xl transition-all border-y border-r border-gray-50 group-hover:border-cyan-50 text-right">
                                            <ButtonInternalLink
                                                className="!rounded-xl !px-4 !py-1.5 text-xs font-bold shadow-sm"
                                                href={`/merchant/sub-vendors/view/${record?.supplierUuid}`}>
                                                {t('payment.table.view')}
                                            </ButtonInternalLink>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-8 py-6 border-t border-gray-100/50 bg-gray-50/30">
                            <PaginationFull paginator={records?.paginatorInfo} get={page} set={setPage}/>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}

export default SubVendors;